
import { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DayList } from "./DayList";
import { DayBadges } from "./DayBadges";
import { DAYS } from "./utils";

type Weekday = Database["public"]["Enums"]["weekday"];

interface DaySelectorProps {
  routineId: string;
  onDayChange?: (days: Weekday[]) => void;
  initialDay?: Weekday;
}

export const DaySelector = ({ routineId, onDayChange, initialDay }: DaySelectorProps) => {
  const [selectedDays, setSelectedDays] = useState<Weekday[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const fetchRoutineDays = async () => {
      console.log("🔍 DaySelector - Fetching days for routine:", routineId);
      
      setIsInitialized(false);
      
      const { data: routineDays, error } = await supabase
        .from('routine_days')
        .select('day')
        .eq('routine_id', routineId);

      if (error) {
        console.error('❌ DaySelector - Error fetching routine days:', error);
        setIsInitialized(true);
        return;
      }

      const days = routineDays?.map(rd => rd.day) || [];
      console.log("📅 DaySelector - Found days:", days, "for routine:", routineId);
      
      // Actualización optimizada del estado
      setSelectedDays(days);
      onDayChange?.(days);
      setIsInitialized(true);
      
      console.log("✅ DaySelector - State updated successfully:", {
        routineId,
        foundDays: days,
        isInitialized: true
      });
    };

    if (routineId) {
      fetchRoutineDays();
    }
  }, [routineId, onDayChange]);

  const handleDayToggle = async (day: Weekday) => {
    try {
      console.log("🔄 DaySelector - Toggling day:", day, "for routine:", routineId);
      
      let newDays: Weekday[];
      
      if (selectedDays.includes(day)) {
        console.log("➖ DaySelector - Removing day:", day);
        const { error } = await supabase
          .from('routine_days')
          .delete()
          .eq('routine_id', routineId)
          .eq('day', day);

        if (error) throw error;

        newDays = selectedDays.filter(d => d !== day);
      } else {
        console.log("➕ DaySelector - Adding day:", day);
        const { error } = await supabase
          .from('routine_days')
          .insert({ routine_id: routineId, day });

        if (error) throw error;

        newDays = [...selectedDays, day];
      }

      // Actualización inmediata y optimizada del estado
      console.log("🚀 DaySelector - Immediate state update:", {
        oldDays: selectedDays,
        newDays,
        change: selectedDays.includes(day) ? 'removed' : 'added',
        day
      });

      setSelectedDays(newDays);
      onDayChange?.(newDays);
      
      console.log("✅ DaySelector - Day toggle completed successfully");
      
      toast({
        title: "Días actualizados",
        description: "Los días de la rutina han sido actualizados correctamente",
      });
    } catch (error) {
      console.error('❌ DaySelector - Error updating routine days:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron actualizar los días de la rutina",
      });
    }
  };

  console.log("🎨 DaySelector - Rendering with optimized state:", {
    routineId,
    selectedDays,
    isInitialized,
    dayCount: selectedDays.length
  });

  return (
    <div className="space-y-2">
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            className="flex items-center gap-2 bg-primary/70 text-white dark:text-white dark:bg-white/5 dark:border-white/20
             dark:hover:bg-white/10 ocean:bg-[#081F2B] ocean:hover:text-white sunset:bg-[#f26018]
             forest:bg-forest/90 forest:text-white"
          >
            <Calendar className="h-4 w-4" />
            Asignar días
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-black bg-opacity-30 backdrop-blur-sm border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Seleccionar días</DialogTitle>
          </DialogHeader>
          <DayList
            selectedDays={selectedDays}
            onDayToggle={handleDayToggle}
            days={DAYS}
          />
        </DialogContent>
      </Dialog>
      
      {/* Renderizado directo e inmediato de los badges */}
      {isInitialized && (
        <DayBadges selectedDays={selectedDays} />
      )}
    </div>
  );
};
